import { MONTHS, type CustomerRevenueReportItem, type Months } from '@vy/types';
import { vyDate } from '@vy/utils';
import type { ChartPeriodMode } from '../../../reports-components/period-mode';

export type { ChartPeriodMode };

export type CustomerRevenueChartDataPoint = {
  label: string;
  recognized: number;
  deferred: number;
  unbilled: number;
};

type MonthlyBucket = CustomerRevenueChartDataPoint & {
  year: number;
  monthIndex: number;
};

export function buildChartData(
  reportItems: CustomerRevenueReportItem[],
  fromDate: Date | null,
  toDate: Date | null,
  periodMode: ChartPeriodMode,
): CustomerRevenueChartDataPoint[] {
  const allMonthly = aggregateAllMonths(reportItems);
  const selected = filterByDateRange(allMonthly, fromDate, toDate);
  const trimmed = trimEmptyEdges(selected);

  return aggregateByPeriod(trimmed, periodMode);
}

function aggregateAllMonths(items: CustomerRevenueReportItem[]): MonthlyBucket[] {
  const buckets = new Map<string, MonthlyBucket>();

  for (const item of items) {
    const yearlyRevenue = item.yearlyRevenue ?? {};

    for (const yearKey of Object.keys(yearlyRevenue)) {
      const year = Number(yearKey);
      const monthsRecord = yearlyRevenue[yearKey];
      if (!monthsRecord) continue;

      for (const month of MONTHS) {
        const breakdown = monthsRecord[month as Months];
        if (!breakdown) continue;

        const monthIndex = MONTHS.indexOf(month);
        const key = monthKey(year, monthIndex);
        const existing = buckets.get(key) ?? {
          label: formatMonthLabel(year, monthIndex),
          year,
          monthIndex,
          recognized: 0,
          deferred: 0,
          unbilled: 0,
        };

        existing.recognized += breakdown.revenue;
        existing.deferred += breakdown.deferredRevenue;
        existing.unbilled += breakdown.unbilledRevenue;

        buckets.set(key, existing);
      }
    }
  }

  return Array.from(buckets.entries())
    .sort(([firstKey], [secondKey]) => firstKey.localeCompare(secondKey))
    .map(([, bucket]) => bucket);
}

function filterByDateRange(
  monthly: MonthlyBucket[],
  fromDate: Date | null,
  toDate: Date | null,
): MonthlyBucket[] {
  const fromMs = fromDate ? toMonthStart(fromDate).getTime() : -Infinity;
  const toMs = toDate ? toMonthStart(toDate).getTime() : Infinity;

  return monthly.filter((bucket) => {
    const bucketMs = new Date(bucket.year, bucket.monthIndex, 1).getTime();
    return bucketMs >= fromMs && bucketMs <= toMs;
  });
}

function trimEmptyEdges(monthly: MonthlyBucket[]): MonthlyBucket[] {
  let firstActiveIndex = -1;
  let lastActiveIndex = -1;

  for (let index = 0; index < monthly.length; index++) {
    if (hasActivity(monthly[index])) {
      if (firstActiveIndex === -1) firstActiveIndex = index;
      lastActiveIndex = index;
    }
  }

  if (firstActiveIndex === -1) return [];

  // Keep one bucket of padding on each side so the chart doesn't start/end flush against a bar.
  const startIndex = Math.max(firstActiveIndex - 1, 0);
  const endIndex = Math.min(lastActiveIndex + 2, monthly.length);

  return monthly.slice(startIndex, endIndex);
}

function aggregateByPeriod(monthly: MonthlyBucket[], mode: ChartPeriodMode): CustomerRevenueChartDataPoint[] {
  if (mode === 'monthly') {
    return monthly.map(toChartDataPoint);
  }

  if (mode === 'quarterly') {
    return bucketBy(monthly, getQuarterLabel);
  }

  return bucketBy(monthly, getAnnualLabel);
}

function bucketBy(
  monthly: MonthlyBucket[],
  getBucketLabel: (bucket: MonthlyBucket) => string,
): CustomerRevenueChartDataPoint[] {
  const bucketByLabel = new Map<string, CustomerRevenueChartDataPoint>();
  const ordered: CustomerRevenueChartDataPoint[] = [];

  for (const monthlyBucket of monthly) {
    const label = getBucketLabel(monthlyBucket);
    let periodBucket = bucketByLabel.get(label);

    if (!periodBucket) {
      periodBucket = { label, recognized: 0, deferred: 0, unbilled: 0 };
      bucketByLabel.set(label, periodBucket);
      ordered.push(periodBucket);
    }

    // Recognized is a flow (sum across the period); deferred and unbilled are
    // end-of-period balances — take the last month's value in each bucket.
    periodBucket.recognized += monthlyBucket.recognized;
    periodBucket.deferred = monthlyBucket.deferred;
    periodBucket.unbilled = monthlyBucket.unbilled;
  }

  return ordered;
}

function hasActivity(bucket: CustomerRevenueChartDataPoint): boolean {
  return bucket.recognized !== 0 || bucket.deferred !== 0 || bucket.unbilled !== 0;
}

function toChartDataPoint(bucket: MonthlyBucket): CustomerRevenueChartDataPoint {
  return {
    label: bucket.label,
    recognized: bucket.recognized,
    deferred: bucket.deferred,
    unbilled: bucket.unbilled,
  };
}

function getQuarterLabel(bucket: MonthlyBucket): string {
  const quarter = Math.floor(bucket.monthIndex / 3) + 1;
  return `Q${quarter} ${formatYearSuffix(bucket.year)}`;
}

function getAnnualLabel(bucket: MonthlyBucket): string {
  return String(bucket.year);
}

function monthKey(year: number, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

function formatMonthLabel(year: number, monthIndex: number): string {
  // `keepLocalTime` builds the month as a UTC wall-clock date so the label isn't shifted on hosts east of UTC.
  return vyDate(`${year}-${String(monthIndex + 1).padStart(2, '0')}-01`, { keepLocalTime: true }).format(
    'MMM YY',
  );
}

function formatYearSuffix(year: number): string {
  return String(year).slice(-2);
}

function toMonthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
